import type { FiscalFamilyId } from "@backchannel/tax-contracts";
import { isWatchedSefaz } from "./watched-sefaz";

/**
 * Where a ledger family lives in Brazilian law. Used to cross the fiscal base
 * with the map without inventing a per-UF family count.
 */
export type FamilyGeography = "federal" | "convenio" | "state_watched" | "municipal";

/**
 * How one UF should read against a focused family. `off` means no family is
 * focused. `federal` and `municipal` are equal overlays. `watched` / `dim`
 * apply only to destination-SEFAZ families.
 */
export type UfWatch = "off" | "watched" | "dim" | "federal" | "municipal";

export function familyGeography(family: FiscalFamilyId): FamilyGeography {
  switch (family) {
    case "NCM":
    case "IPI":
    case "PIS_COFINS":
      return "federal";
    case "CEST":
      return "convenio";
    case "ICMS":
    case "ICMS_ST":
    case "MVA":
      return "state_watched";
    case "ISS":
      return "municipal";
    default: {
      const _exhaustive: never = family;
      return _exhaustive;
    }
  }
}

/**
 * Reads a family from captured tax tags. Returns null when the tags do not
 * name a ledger family. Never infers a rate.
 */
export function familyFromTags(tags: readonly string[]): FiscalFamilyId | null {
  const names = tags.map(
    (tag) => tag.split(":")[0]?.trim().toLowerCase().replaceAll("_", "-") ?? "",
  );
  if (names.some((tag) => tag === "icms-st" || tag === "substituicao-tributaria")) {
    return "ICMS_ST";
  }
  if (names.includes("mva")) return "MVA";
  if (names.includes("ipi")) return "IPI";
  if (names.includes("cest")) return "CEST";
  if (names.includes("ncm")) return "NCM";
  if (names.some((tag) => tag === "aliquota-interna" || tag === "difal" || tag === "icms")) {
    return "ICMS";
  }
  if (names.some((tag) => tag.includes("pis") || tag.includes("cofins"))) return "PIS_COFINS";
  if (names.includes("iss")) return "ISS";
  return null;
}

export function ufWatchForFamily(family: FiscalFamilyId | null, uf: string): UfWatch {
  if (family === null) return "off";
  const geography = familyGeography(family);
  if (geography === "state_watched") return isWatchedSefaz(uf) ? "watched" : "dim";
  if (geography === "municipal") return "municipal";
  return "federal";
}
